/**
 * JSON-LD structured data builders.
 *
 * All URLs are absolute and built from `env.SITE_URL` so the markup is valid
 * when pages are prerendered and when crawlers fetch them directly.
 */
import { env } from '@/lib/env';

type Json = Record<string, unknown>;

/**
 * Locale-relative absolute URL with a trailing slash, matching what the S3
 * static-website origin serves with a 200 (directory-style objects). Keeping
 * JSON-LD URLs slash-consistent with the canonical/sitemap avoids advertising
 * URLs that 302-redirect. See `withTrailingSlash`/`localeAbsPath` in
 * `components/seo/meta.ts` for the same convention applied to canonical tags.
 */
function localeUrl(locale: string, path: string): string {
    if (!path || path === '/') return `${env.SITE_URL}/${locale}/`;
    const normalized = path.startsWith('/') ? path : `/${path}`;
    const withSlash = normalized.endsWith('/') ? normalized : `${normalized}/`;
    return `${env.SITE_URL}/${locale}${withSlash}`;
}

const ORG_NAME = 'Stork Rentals';
const ORG_LEGAL_NAME = 'The Stork Exchange Rentals';
const ORG_DESCRIPTION =
    'Premium baby equipment rental — clean, safe strollers, cribs and car seats for travel and everyday family life.';
const ORG_LOGO = `${env.SITE_URL}/assets/images/logo/TSELogoFull.png`;

/** Stable @id for the Organization so other nodes can reference it. */
export const ORG_ID = `${env.SITE_URL}/#organization`;
export const WEBSITE_ID = `${env.SITE_URL}/#website`;

export function organizationSchema(): Json {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': ORG_ID,
        name: ORG_NAME,
        legalName: ORG_LEGAL_NAME,
        url: env.SITE_URL,
        logo: ORG_LOGO,
        image: ORG_LOGO,
        description: ORG_DESCRIPTION,
        email: env.SUPPORT_EMAIL,
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            email: env.SUPPORT_EMAIL,
            availableLanguage: ['English'],
        },
    };
}

export function webSiteSchema(defaultLocale: string): Json {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        name: ORG_NAME,
        url: env.SITE_URL,
        publisher: { '@id': ORG_ID },
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${localeUrl(defaultLocale, '/catalogue')}?search={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
        },
    };
}

export interface BreadcrumbItem {
    name: string;
    /** Locale-relative path, e.g. "/catalogue". */
    path: string;
}

export function breadcrumbSchema(items: BreadcrumbItem[], locale: string): Json {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: localeUrl(locale, item.path),
        })),
    };
}

export interface FaqEntry {
    question: string;
    answer: string;
}

export function faqSchema(entries: FaqEntry[]): Json {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entries.map((entry) => ({
            '@type': 'Question',
            name: entry.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: entry.answer,
            },
        })),
    };
}

export interface LocalBusinessInput {
    name: string;
    /** Locale-relative path of the location page. */
    path: string;
    description?: string;
    image?: string;
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
    latitude?: number;
    longitude?: number;
    telephone?: string;
}

export function localBusinessSchema(input: LocalBusinessInput, locale: string): Json {
    const address: Json = { '@type': 'PostalAddress' };
    if (input.streetAddress) address.streetAddress = input.streetAddress;
    if (input.addressLocality) address.addressLocality = input.addressLocality;
    if (input.addressRegion) address.addressRegion = input.addressRegion;
    if (input.postalCode) address.postalCode = input.postalCode;
    address.addressCountry = input.addressCountry ?? 'IE';

    const schema: Json = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: `${ORG_NAME} — ${input.name}`,
        url: localeUrl(locale, input.path),
        parentOrganization: { '@id': ORG_ID },
        address,
    };
    if (input.description) schema.description = input.description;
    if (input.image) schema.image = input.image;
    if (input.telephone) schema.telephone = input.telephone;
    if (typeof input.latitude === 'number' && typeof input.longitude === 'number') {
        schema.geo = {
            '@type': 'GeoCoordinates',
            latitude: input.latitude,
            longitude: input.longitude,
        };
    }
    return schema;
}
